import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, forkJoin, map, of, Subscription } from 'rxjs';
import { SubmissionTableLabPreferencesService } from './submission-table-lab-preferences.service';
import {
  SUBMISSION_TABLE_LAB_ADAPTER,
  SubmissionTableLabLoadPayload,
  SubmissionTableLabLoadResponse,
  SubmissionTableLabStatusResponse
} from './submission-table-lab.adapter';
import {
  SUBMISSION_TABLE_LAB_APPLICATION_OPTIONS,
  SUBMISSION_TABLE_LAB_COLUMN_OPTIONS,
  SUBMISSION_TABLE_LAB_COLUMN_ORDER,
  SUBMISSION_TABLE_LAB_STATUS_OPTIONS,
  SubmissionTableLabActiveFilterItem,
  SubmissionTableLabColumnKey,
  SubmissionTableLabFilters,
  SubmissionTableLabRow,
  SubmissionTableLabSortState
} from './submission-table-lab.models';
import { createSubmissionTableLabHeaderWidths } from './submission-table-lab.header.util';

type ProgressRingSegment = {
  index: number;
  color: string;
  dasharray: string;
  dashoffset: number;
};

type SubmissionSearchPayloadKey =
  | 'submission_id'
  | 'submit_date'
  | 'insured_name'
  | 'underwriter'
  | 'short_name'
  | 'policy_number'
  | 'lob'
  | 'quote_desc'
  | 'treaty_description'
  | 'risk_appetite'
  | 'bind_propensity'
  | 'data_quality_score'
  | 'status_id_int'
  | 'effective_date'
  | 'premium';

type SubmissionTableLabQueueMode = 'all' | 'PROCESSED' | 'PROCEED' | 'FLAGGED' | 'DECLINE';
type SubmissionTableLabApiQueueMode = 'PROCESSED' | 'PROCEED' | 'FLAGGED' | 'DECLINE';
type SubmissionTableLabRecord = Record<string, unknown>;

const PAGE_LIMIT_OPTIONS = [10, 15, 30, 50, 100];
const FILTER_DEBOUNCE_MS = 400;

const DEFAULT_SUBMISSIONS_PAYLOAD: Omit<SubmissionTableLabLoadPayload, 'user_id'> = {
  page: 1,
  limit: 10,
  filter_flag: 1,
  underwriter: '',
  underwriter_id: '',
  include_deleted: 0,
  submission_id: '',
  quote_desc: '',
  policy_number: '',
  status: '',
  submit_date: '',
  effective_date: '',
  premium: '',
  insured_id: '',
  dot_number: '',
  insured_name: '',
  agency: '',
  claim_count: '',
  treaty_description: '',
  exp_date: '',
  cancellation_date: '',
  lob: '',
  risk_company: '',
  source_portal: '',
  agency_id: '',
  agency_state: '',
  treaty_id_int: '',
  status_id_int: [1, 3, 5, 7, 17, 18, 27, 28, 29, 30, 62, 4],
  short_name: [499, 485, 492, 495, 483],
  quote_id: '',
  risk_appetite: '',
  bind_propensity: '',
  data_quality_score: ''
};

const SUBMISSION_FILTER_PAYLOAD_MAP: Partial<Record<SubmissionTableLabColumnKey, SubmissionSearchPayloadKey>> = {
  id: 'submission_id',
  submitDate: 'submit_date',
  insured: 'insured_name',
  underwriter: 'underwriter',
  quoteId: 'quote_desc',
  submissionStatus: 'status_id_int',
  applicationType: 'short_name',
  policyNumber: 'policy_number',
  line: 'lob',
  submissionType: 'treaty_description',
  riskAppetite: 'risk_appetite',
  bindPropensity: 'bind_propensity',
  dataQualityScore: 'data_quality_score',
  effectiveDate: 'effective_date',
  annualRevenue: 'premium'
};

const CLIENT_SIDE_ONLY_FILTER_KEYS: SubmissionTableLabColumnKey[] = [
  'riskAppetite',
  'bindPropensity',
  'dataQualityScore',
  'evaluationStatus'
];

@Injectable({ providedIn: 'root' })
export class SubmissionTableLabService {
  private readonly adapter = inject(SUBMISSION_TABLE_LAB_ADAPTER);
  private readonly tablePreferences = inject(SubmissionTableLabPreferencesService);

  readonly rows = signal<SubmissionTableLabRow[]>([]);
  readonly filters = signal<SubmissionTableLabFilters>({});
  readonly sortState = signal<SubmissionTableLabSortState | null>(null);
  readonly pageLimit = signal(10);
  readonly currentPage = signal(1);
  readonly totalCount = signal(0);
  readonly tableStateMessage = signal('Loading Submissions...');
  readonly tableStateTone = signal<'loading' | 'empty' | 'error'>('loading');
  readonly actionFilter = signal<SubmissionTableLabQueueMode>('all');
  readonly tableColumnVisibility = signal<Record<SubmissionTableLabColumnKey, boolean>>(
    this.tablePreferences.getVisibilityForUser(this.adapter.getUserId())
  );
  readonly tableColumnOrder = signal<SubmissionTableLabColumnKey[]>(
    [...this.tablePreferences.getOrderForUser(this.adapter.getUserId())]
  );
  readonly tableHeaderWidths = signal(createSubmissionTableLabHeaderWidths());
  readonly canToggleUnderwriterScope = computed(() => this.adapter.getCurrentUserUnderwriterFilter().trim().length > 0);
  readonly showAllSubmissions = computed(() =>
    this.canToggleUnderwriterScope() && ((this.filters().underwriter ?? '').trim().length === 0)
  );
  readonly transientSocketStatus = this.adapter.transientSocketStatus;
  readonly pageLimitOptions = PAGE_LIMIT_OPTIONS;
  readonly normalizedFilters = computed(() => this.normalizeFilters(this.filters()));
  readonly filteredRows = computed(() => this.applyFilters(this.rows(), this.normalizedFilters()));
  readonly sortedRows = computed(() => this.applySort(this.filteredRows(), this.sortState()));
  readonly visibleRows = computed(() => this.sortedRows());
  readonly hasActiveFilters = computed(() => Object.keys(this.normalizedFilters()).length > 0);
  readonly activeFilterItems = computed(() =>
    (Object.entries(this.normalizedFilters()) as Array<[SubmissionTableLabColumnKey, string]>)
      .map(([key, value]) => this.buildActiveFilterItem(key, value))
      .filter((item): item is SubmissionTableLabActiveFilterItem => item !== null)
  );
  readonly activeFilterValueCount = computed(() =>
    this.activeFilterItems().reduce((count, item) => count + item.values.length, 0)
  );
  readonly totalPages = computed(() => {
    const limit = this.pageLimit();

    if (limit <= 0) {
      return 1;
    }

    return Math.max(1, Math.ceil(this.totalCount() / limit));
  });
  readonly pagerItems = computed(() => this.buildPagerItems(this.currentPage(), this.totalPages()));

  private activeLoadRequestId = 0;
  private filterDebounceHandle?: ReturnType<typeof setTimeout>;
  private submissionsLoadSubscription?: Subscription;
  private submissionStatusSubscription?: Subscription;

  initialize(): void {
    this.cancelScheduledLoad();
    this.submissionsLoadSubscription?.unsubscribe();
    this.submissionStatusSubscription?.unsubscribe();
    this.currentPage.set(1);
    this.pageLimit.set(10);
    this.sortState.set(null);
    this.filters.set(this.getDefaultColumnFilters());
    this.tableColumnVisibility.set(this.tablePreferences.getVisibilityForUser(this.adapter.getUserId()));
    this.tableColumnOrder.set([...this.tablePreferences.getOrderForUser(this.adapter.getUserId())]);
    this.totalCount.set(0);
    this.rows.set([]);
    this.tableStateMessage.set('Loading Submissions...');
    this.tableStateTone.set('loading');
    this.loadRows();
  }

  dispose(): void {
    this.cancelScheduledLoad();
    this.submissionsLoadSubscription?.unsubscribe();
    this.submissionStatusSubscription?.unsubscribe();
    this.activeLoadRequestId += 1;
  }

  updatePageLimit(limit: number): void {
    if (!Number.isFinite(limit) || limit <= 0) {
      return;
    }

    if (this.pageLimit() === limit) {
      return;
    }

    this.cancelScheduledLoad();
    this.pageLimit.set(limit);
    this.currentPage.set(1);
    this.loadRows();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) {
      return;
    }

    this.currentPage.set(page);
    this.loadRows();
  }

  updateFilter(key: SubmissionTableLabColumnKey, value: string): void {
    const nextValue = value.trim();
    const currentValue = this.filters()[key] ?? '';

    if (currentValue === nextValue) {
      return;
    }

    this.filters.update((filters) => this.withFilterValue(filters, key, nextValue));
    this.currentPage.set(1);

    if (this.isClientSideOnlyFilterKey(key) || this.isActionFilterMode()) {
      return;
    }

    this.scheduleLoad();
  }

  clearFilter(key: SubmissionTableLabColumnKey): void {
    if (!this.filters()[key]) {
      return;
    }

    this.filters.update((filters) => this.withFilterValue(filters, key, ''));
    this.currentPage.set(1);

    if (this.isClientSideOnlyFilterKey(key) || this.isActionFilterMode()) {
      return;
    }

    this.scheduleLoad();
  }

  clearAllFilters(): void {
    this.cancelScheduledLoad();
    this.filters.set(this.getDefaultColumnFilters());
    this.currentPage.set(1);
    this.loadRows();
  }

  updateActionFilter(value: SubmissionTableLabQueueMode): void {
    if (this.actionFilter() === value) {
      return;
    }

    this.cancelScheduledLoad();
    this.actionFilter.set(value);
    this.currentPage.set(1);
    this.loadRows();
  }

  toggleUnderwriterScope(): void {
    const currentUserUnderwriterFilter = this.currentUserUnderwriterFilter();

    if (!currentUserUnderwriterFilter) {
      return;
    }

    const nextFilters = this.showAllSubmissions()
      ? this.withFilterValue(this.filters(), 'underwriter', currentUserUnderwriterFilter)
      : this.withFilterValue(this.filters(), 'underwriter', '');

    this.cancelScheduledLoad();
    this.filters.set(nextFilters);
    this.currentPage.set(1);
    this.loadRows();
  }

  setSort(key: SubmissionTableLabColumnKey): void {
    this.sortState.update((current) => {
      if (current?.key !== key) {
        return { key, direction: 'asc' };
      }

      return {
        key,
        direction: current.direction === 'asc' ? 'desc' : 'asc'
      };
    });
  }

  getSortIconName(key: SubmissionTableLabColumnKey): string {
    const current = this.sortState();

    if (current?.key !== key) {
      return 'sliders-horizontal';
    }

    return current.direction === 'asc' ? 'arrow-up' : 'arrow-down';
  }

  setColumnVisibility(key: SubmissionTableLabColumnKey, visible: boolean): void {
    this.tableColumnVisibility.update((visibility) => ({
      ...visibility,
      [key]: visible
    }));

    this.tablePreferences.saveVisibilityForUser(this.adapter.getUserId(), this.tableColumnVisibility());
  }

  hasColumnVisible(key: SubmissionTableLabColumnKey): boolean {
    return this.tableColumnVisibility()[key] !== false;
  }

  moveColumn(sourceKey: SubmissionTableLabColumnKey, targetKey: SubmissionTableLabColumnKey): void {
    this.tableColumnOrder.update((current) => {
      if (sourceKey === targetKey) {
        return current;
      }

      const order = current.filter((key) => key !== sourceKey);
      const targetIndex = order.indexOf(targetKey);

      if (targetIndex < 0) {
        return current;
      }

      order.splice(targetIndex, 0, sourceKey);
      return order;
    });

    this.tablePreferences.saveOrderForUser(
      this.adapter.getUserId(),
      this.tableColumnVisibility(),
      this.tableColumnOrder()
    );
  }

  resetColumns(): void {
    this.tableColumnVisibility.set(this.createDefaultVisibility());
    this.tableColumnOrder.set([...SUBMISSION_TABLE_LAB_COLUMN_ORDER]);
    this.tableHeaderWidths.set(createSubmissionTableLabHeaderWidths());
    this.tablePreferences.resetForUser(this.adapter.getUserId());
  }

  getColumnLabel(key: SubmissionTableLabColumnKey): string {
    return SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.find((column) => column.key === key)?.label ?? key;
  }

  getColumnWidth(key: SubmissionTableLabColumnKey | 'action'): number {
    if (key === 'action') {
      return 88;
    }

    return SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.find((column) => column.key === key)?.width ?? 140;
  }

  getColumnMinWidth(key: SubmissionTableLabColumnKey | 'action'): number {
    if (key === 'action') {
      return 88;
    }

    return SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.find((column) => column.key === key)?.minWidth ?? 120;
  }

  getColumnMaxWidth(key: SubmissionTableLabColumnKey | 'action'): number {
    if (key === 'action') {
      return 88;
    }

    return SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.find((column) => column.key === key)?.maxWidth ?? 220;
  }

  getFilterValues(key: SubmissionTableLabColumnKey): string[] {
    const value = this.filters()[key]?.trim() ?? '';

    if (!value) {
      return [];
    }

    if (key === 'submissionStatus' || key === 'applicationType') {
      return value.split(',').map((item) => item.trim()).filter(Boolean);
    }

    return [value];
  }

  setApplicationTypeFilter(option: string): void {
    const currentValues = this.getFilterValues('applicationType');
    const nextValues = currentValues.includes(option)
      ? currentValues.filter((value) => value !== option)
      : SUBMISSION_TABLE_LAB_APPLICATION_OPTIONS.filter((value) => value === option || currentValues.includes(value));

    this.updateFilter('applicationType', nextValues.join(', '));
  }

  setStatusFilter(option: string): void {
    const currentValues = this.getFilterValues('submissionStatus');
    const nextValues = currentValues.includes(option)
      ? currentValues.filter((value) => value !== option)
      : SUBMISSION_TABLE_LAB_STATUS_OPTIONS.filter((value) => value === option || currentValues.includes(value));

    this.updateFilter('submissionStatus', nextValues.join(', '));
  }

  private createDefaultVisibility(): Record<SubmissionTableLabColumnKey, boolean> {
    return Object.fromEntries(
      SUBMISSION_TABLE_LAB_COLUMN_OPTIONS.map((option) => [option.key, option.defaultSelected] as const)
    ) as Record<SubmissionTableLabColumnKey, boolean>;
  }

  openSubmissionDetails(row: SubmissionTableLabRow): void {
    const actionStatus = this.getRowActionStatus(row);

    if (this.isActionTemporarilyBlocked(row) || this.isActionDisabled(row)) {
      return;
    }

    if (actionStatus?.isProcessed === true) {
      this.adapter.openProcessedSubmission(row);

      return;
    }

    if (actionStatus?.hasDocument === true) {
      this.adapter.startSubmissionProcessing(row);
    }
  }

  isCheckingDocuments(row: SubmissionTableLabRow): boolean {
    return this.hasCheckableQuoteId(row.quoteId) && row.isCheckingActionStatus === true;
  }

  isOpeningSubmission(row: SubmissionTableLabRow): boolean {
    return this.adapter.isOpeningSubmission(row.id);
  }

  isActionTemporarilyBlocked(row: SubmissionTableLabRow): boolean {
    return this.adapter.isProcessingSubmission(row.id);
  }

  isActionDisabled(row: SubmissionTableLabRow): boolean {
    if (!this.hasCheckableQuoteId(row.quoteId)) {
      return true;
    }

    if (this.isCheckingDocuments(row)) {
      return true;
    }

    if (this.isOpeningSubmission(row)) {
      return true;
    }

    const status = this.getRowActionStatus(row);
    return !status || status.hasDocument !== true;
  }

  showActionProgress(row: SubmissionTableLabRow): boolean {
    return this.adapter.isProcessingSubmission(row.id);
  }

  showActionProcessed(row: SubmissionTableLabRow): boolean {
    if (this.isActionTemporarilyBlocked(row) || this.isCheckingDocuments(row) || this.isOpeningSubmission(row)) {
      return false;
    }

    return this.getRowActionStatus(row)?.isProcessed === true;
  }

  getActionProgressValue(row: SubmissionTableLabRow): number {
    return this.adapter.getProcessingProgress(row.id);
  }

  getActionProgressStageLabel(row: SubmissionTableLabRow): string {
    const total = this.getActionProgressStageTotal(row);
    const completed = this.getActionProgressCompletedStages(row);
    return `${completed}/${total}`;
  }

  getActionProgressRingSegments(row: SubmissionTableLabRow): ProgressRingSegment[] {
    return this.getProgressRingSegments(this.getActionProgressValue(row), row.statusStageTotal, 'var(--cp-primary)');
  }

  getActionCompletedRingSegments(row: SubmissionTableLabRow): ProgressRingSegment[] {
    switch ((row.evaluationStatus ?? '').trim().toUpperCase()) {
      case 'PROCEED':
        return this.getProgressRingSegments(100, row.statusStageTotal, 'var(--cp-success)');
      case 'FLAGGED':
        return this.getProgressRingSegments(100, row.statusStageTotal, 'var(--cp-warning)');
      case 'DECLINE':
        return this.getProgressRingSegments(100, row.statusStageTotal, 'var(--cp-danger)');
      default:
        return this.getProgressRingSegments(100, row.statusStageTotal, 'var(--cp-success)');
    }
  }

  private getProgressRingSegments(progress: number, stageTotal: number, activeColor: string): ProgressRingSegment[] {
    const total = Math.max(1, Math.floor(stageTotal) || 1);
    const completed = progress <= 0
      ? 0
      : Math.min(total, Math.ceil((Math.max(0, Math.min(100, progress)) / 100) * total));
    const radius = 9;
    const circumference = 2 * Math.PI * radius;
    const segmentLength = circumference / total;
    const gapLength = Math.min(1.2, segmentLength * 0.16);
    const dashLength = Math.max(0.1, segmentLength - gapLength);
    const inactiveColor = 'var(--cp-border-strong)';

    return Array.from({ length: total }, (_, index) => ({
      index,
      color: index < completed ? activeColor : inactiveColor,
      dasharray: `${dashLength} ${circumference}`,
      dashoffset: -(index * segmentLength),
    }));
  }

  private getActionProgressStageTotal(row: SubmissionTableLabRow): number {
    return Math.max(1, Math.floor(row.statusStageTotal || 0));
  }

  private getActionProgressCompletedStages(row: SubmissionTableLabRow): number {
    const progress = this.getActionProgressValue(row);
    const total = this.getActionProgressStageTotal(row);

    if (progress <= 0) {
      return 0;
    }

    return Math.min(total, Math.ceil((Math.max(0, Math.min(100, progress)) / 100) * total));
  }

  getActionProgressRingBackground(row: SubmissionTableLabRow): string {
    return '';
  }

  getActionCompletedRingBackground(row: SubmissionTableLabRow): string {
    return '';
  }

  getProcessedCheckIcon(row: SubmissionTableLabRow): string {
    switch ((row.evaluationStatus ?? '').trim().toUpperCase()) {
      case 'PROCEED':
        return 'check';
      case 'FLAGGED':
        return 'alert-triangle';
      case 'DECLINE':
        return 'x';
      default:
        return 'check';
    }
  }

  getCheckColor(row: SubmissionTableLabRow): string {
    switch ((row.evaluationStatus ?? '').trim().toUpperCase()) {
      case 'PROCEED':
        return '#22c55e';
      case 'FLAGGED':
        return '#FFA51D';
      case 'DECLINE':
        return '#FF3B30';
      default:
        return '#22c55e';
    }
  }

  getActionTitle(row: SubmissionTableLabRow): string {
    if (!this.hasCheckableQuoteId(row.quoteId)) {
      return 'Quote is unavailable';
    }

    if (this.isCheckingDocuments(row)) {
      return 'Checking submission status';
    }

    if (this.isOpeningSubmission(row)) {
      return 'Loading submission insights';
    }

    if (this.isActionTemporarilyBlocked(row)) {
      return 'Submission processing in progress';
    }

    const status = this.getRowActionStatus(row);

    if (!status) {
      return 'Checking submission status';
    }

    if (!status.hasDocument) {
      return 'No documents available';
    }

    if (!status.isProcessed) {
      return 'Start processing for submission insights';
    }

    const evaluationStatus = this.getEvaluationStatusLabel(row);

    return evaluationStatus
      ? `Open submission insight details. Evaluation status: ${evaluationStatus}.`
      : 'Open submission insight details.';
  }

  evaluationStatusClasses(value: string | null | undefined): string {
    const normalized = (value ?? '').trim().toLowerCase();
    const base = 'inline-flex rounded-full px-2.5 py-1 text-[10.5px] font-semibold';

    if (!normalized || normalized === 'n/a') {
      return `${base} bg-surface-sunken text-text-muted`;
    }

    if (['decline', 'failed', 'reject', 'rejected'].includes(normalized)) {
      return `${base} bg-danger-soft text-danger`;
    }

    if (['flag', 'flagged'].includes(normalized)) {
      return `${base} bg-warning-soft text-warning`;
    }

    if (['approve', 'approved', 'accept', 'accepted', 'success'].includes(normalized)) {
      return `${base} bg-success-soft text-success`;
    }

    return `${base} bg-primary-soft text-primary`;
  }

  private loadRows(): void {
    this.activeLoadRequestId += 1;
    const requestId = this.activeLoadRequestId;
    this.submissionsLoadSubscription?.unsubscribe();
    this.submissionStatusSubscription?.unsubscribe();
    this.tableStateMessage.set(this.getLoadingMessage());
    this.tableStateTone.set('loading');

    if (!this.isActionFilterMode()) {
      this.submissionsLoadSubscription = this.adapter.loadSubmissions(this.buildPayload()).pipe(
        map((response) => this.mapRows(response))
      ).subscribe({
        next: (rows) => {
          if (requestId !== this.activeLoadRequestId) {
            return;
          }

          this.setRowsAndCache(rows);
          this.fetchSubmissionStatuses(rows, requestId);
        },
        error: (error) => {
          if (requestId !== this.activeLoadRequestId) {
            return;
          }

          console.error('Error fetching submissions:', error);
          this.rows.set([]);
          this.totalCount.set(0);
          this.tableStateMessage.set(this.getLoadErrorMessage(error));
          this.tableStateTone.set('error');
        }
      });
      return;
    }

    this.submissionsLoadSubscription = this.adapter.listSubmissionsByFilter({
      filter: this.getSelectedActionFilterForApi(),
      page: this.currentPage(),
      limit: this.pageLimit()
    }).subscribe({
      next: (response) => {
        if (requestId !== this.activeLoadRequestId) {
          return;
        }

        this.totalCount.set(response.total_count ?? 0);
        this.currentPage.set(response.page ?? this.currentPage());
        this.pageLimit.set(response.limit ?? this.pageLimit());

        const submissionIds = (response.data ?? [])
          .map((item:any) => this.normalizeSubmissionId(item.submission_id))
          .filter((submissionId:any): submissionId is string => submissionId.length > 0);

        if (!submissionIds.length) {
          this.rows.set([]);
          this.tableStateMessage.set('No Submissions Found.');
          this.tableStateTone.set('empty');
          return;
        }

        this.hydrateActionFilterRows(submissionIds, requestId);
      },
      error: (error) => {
        if (requestId !== this.activeLoadRequestId) {
          return;
        }

        console.error('Error fetching action-filtered submissions:', error);
        this.rows.set([]);
        this.totalCount.set(0);
        this.tableStateMessage.set(this.getLoadErrorMessage(error));
        this.tableStateTone.set('error');
      }
    });
  }

  private getRowActionStatus(row: SubmissionTableLabRow): SubmissionTableLabRow['actionStatus'] {
    return this.adapter.getRowActionStatus(row);
  }

  private hydrateActionFilterRows(submissionIds: string[], requestId: number): void {
    const requests = submissionIds.map((submissionId) =>
      this.adapter.loadSubmissions(this.buildSubmissionDetailPayload(submissionId)).pipe(
        map((response) => ({
          submissionId,
          records: this.extractSubmissionRecords(response)
        }))
      )
    );

    if (!requests.length) {
      this.rows.set([]);
      this.tableStateMessage.set('No Submissions Found.');
      this.tableStateTone.set('empty');
      return;
    }

    this.submissionsLoadSubscription = forkJoin(requests).subscribe({
      next: (results) => {
        if (requestId !== this.activeLoadRequestId) {
          return;
        }

        const recordsBySubmissionId = new Map<string, SubmissionTableLabRecord>();

        for (const result of results) {
          const [record] = result.records;

          if (record) {
            recordsBySubmissionId.set(result.submissionId, record);
          }
        }

        const rows = submissionIds
          .map((submissionId, index) => {
            const record = recordsBySubmissionId.get(submissionId);

            return record ? this.mapRecordToRow(record, index) : null;
          })
          .filter((row): row is SubmissionTableLabRow => row !== null);

        this.setRowsAndCache(rows);
        this.fetchSubmissionStatuses(rows, requestId);
      },
      error: (error) => {
        if (requestId !== this.activeLoadRequestId) {
          return;
        }

        console.error('Error hydrating submissions for action filter:', error);
        this.rows.set([]);
        this.totalCount.set(0);
        this.tableStateMessage.set(this.getLoadErrorMessage(error));
        this.tableStateTone.set('error');
      }
    });
  }

  private fetchSubmissionStatuses(rows: SubmissionTableLabRow[], requestId: number): void {
    const rowsToCheck = rows.filter((row) => this.hasCheckableQuoteId(row.quoteId));

    if (!rowsToCheck.length) {
      return;
    }

    const requests = rowsToCheck.map((row) =>
      this.adapter.getSubmissionStatus(row.id, row.quoteId).pipe(
        map((response) => ({
          row,
          actionStatus: this.mapSubmissionActionStatus(response)
        })),
        catchError(() => of({
          row,
          actionStatus: {
            isProcessed: false,
            hasDocument: false,
            riskAppetiteScore: null,
            evaluationStatus: 'N/A'
          }
        }))
      )
    );

    this.submissionStatusSubscription?.unsubscribe();
    this.submissionStatusSubscription = forkJoin(requests).subscribe((results) => {
      if (requestId !== this.activeLoadRequestId) {
        return;
      }

      const updatedRows = this.rows().map((currentRow) => {
        const matched = results.find((item) => item.row.id === currentRow.id);

        if (!matched) {
          return currentRow;
        }

        const actionStatus = matched.actionStatus;

        if (!actionStatus) {
          return currentRow;
        }

        return {
          ...currentRow,
          riskAppetite: actionStatus.riskAppetiteScore ?? currentRow.riskAppetite,
          evaluationStatus: actionStatus.evaluationStatus ?? currentRow.evaluationStatus,
          actionStatus,
          isCheckingActionStatus: false
        };
      });

      this.rows.set(updatedRows);
      this.tableStateMessage.set(updatedRows.length ? '' : 'No Submissions Found.');
      this.tableStateTone.set('empty');
    });
  }

  private setRowsAndCache(rows: SubmissionTableLabRow[]): void {
    this.rows.set(rows);
    this.tableStateMessage.set(rows.length ? '' : 'No Submissions Found.');
    this.tableStateTone.set('empty');
  }

  private scheduleLoad(): void {
    this.cancelScheduledLoad();

    this.filterDebounceHandle = setTimeout(() => {
      this.filterDebounceHandle = undefined;
      this.loadRows();
    }, FILTER_DEBOUNCE_MS);
  }

  private cancelScheduledLoad(): void {
    if (!this.filterDebounceHandle) {
      return;
    }

    clearTimeout(this.filterDebounceHandle);
    this.filterDebounceHandle = undefined;
  }

  private buildPayload(): SubmissionTableLabLoadPayload {
    return {
      ...DEFAULT_SUBMISSIONS_PAYLOAD,
      ...this.buildSearchPayload(),
      ...this.buildRoleScopedPayload(),
      page: this.currentPage(),
      limit: this.pageLimit(),
      user_id: this.getUserId()
    };
  }

  private buildSearchPayload(): Partial<SubmissionTableLabLoadPayload> {
    const filters = this.normalizeFilters(this.filters());
    const payload: Partial<SubmissionTableLabLoadPayload> = {};

    for (const [key, value] of Object.entries(filters) as Array<[SubmissionTableLabColumnKey, string]>) {
      if (this.isClientSideOnlyFilterKey(key) || this.isActionFilterMode()) {
        continue;
      }

      const payloadKey = SUBMISSION_FILTER_PAYLOAD_MAP[key];

      if (!payloadKey) {
        continue;
      }

      if (payloadKey === 'short_name') {
        payload.short_name = this.parseApplicationTypeFilter(value);
        continue;
      }

      if (payloadKey === 'status_id_int') {
        payload.status_id_int = this.parseStatusFilter(value);
        continue;
      }

      (payload as Record<string, unknown>)[payloadKey] = value;
    }

    return payload;
  }

  private buildRoleScopedPayload(): Pick<SubmissionTableLabLoadPayload, 'underwriter_id'> {
    return {
      underwriter_id: ''
    };
  }

  private getLoadErrorMessage(error: unknown): string {
    const supportHint = 'Connect With Technical Team.';
    const fallback = 'Unable To Load Submissions Right Now.';

    if (this.isActionFilterMode()) {
      return `Unable To Load ${this.toTitleCase(this.getSelectedActionFilter())} Submissions Right Now.\n${supportHint}`;
    }

    if (error && typeof error === 'object') {
      const possibleMessage = 'message' in error ? (error as { message?: unknown }).message : undefined;
      if (typeof possibleMessage === 'string' && possibleMessage.trim()) {
        return `${fallback}\n${supportHint}`;
      }
    }

    return `${fallback}\n${supportHint}`;
  }

  private getLoadingMessage(): string {
    return this.isActionFilterMode()
      ? `Loading ${this.toTitleCase(this.getSelectedActionFilter())} Submissions...`
      : 'Loading Submissions...';
  }

  private getSelectedActionFilter(): SubmissionTableLabQueueMode {
    return this.actionFilter();
  }

  private getSelectedActionFilterForApi(): SubmissionTableLabApiQueueMode {
    switch (this.getSelectedActionFilter()) {
      case 'PROCESSED':
        return 'PROCESSED';
      case 'PROCEED':
        return 'PROCEED';
      case 'FLAGGED':
        return 'FLAGGED';
      case 'DECLINE':
        return 'DECLINE';
      default:
        return 'PROCESSED';
    }
  }

  private isActionFilterMode(): boolean {
    return this.actionFilter() !== 'all';
  }

  private getUserId(): number {
    return this.adapter.getUserId() ?? 10413;
  }

  private getDefaultColumnFilters(): SubmissionTableLabFilters {
    return {};
  }

  private currentUserUnderwriterFilter(): string {
    return this.adapter.getCurrentUserUnderwriterFilter();
  }

  private normalizeFilters(filters: SubmissionTableLabFilters): SubmissionTableLabFilters {
    return Object.fromEntries(
      Object.entries(filters)
        .map(([key, value]) => [key, value.trim()] as const)
        .filter(([, value]) => value.length > 0)
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    ) as SubmissionTableLabFilters;
  }

  private withFilterValue(
    filters: SubmissionTableLabFilters,
    key: SubmissionTableLabColumnKey,
    value: string
  ): SubmissionTableLabFilters {
    const nextValue = value.trim();

    if (!nextValue) {
      const { [key]: _removed, ...remaining } = filters;
      return remaining;
    }

    return {
      ...filters,
      [key]: nextValue
    };
  }

  private isClientSideOnlyFilterKey(key: SubmissionTableLabColumnKey): boolean {
    return CLIENT_SIDE_ONLY_FILTER_KEYS.includes(key);
  }

  private parseApplicationTypeFilter(value: string): number[] {
    return value
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isFinite(item));
  }

  private parseStatusFilter(value: string): number[] {
    return value
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isFinite(item));
  }

  private mapRows(response: SubmissionTableLabLoadResponse): SubmissionTableLabRow[] {
    const data = response.data;

    this.totalCount.set(data?.total_count ?? 0);
    this.currentPage.set(data?.page ?? this.currentPage());
    this.pageLimit.set(data?.limit ?? this.pageLimit());

    return (data?.records ?? [])
      .map((record:any, index:number) => this.mapRecordToRow(record, index))
      .sort((left:any, right:any) => right.id.localeCompare(left.id));
  }

  private extractSubmissionRecords(response: SubmissionTableLabLoadResponse): SubmissionTableLabRecord[] {
    return Array.isArray(response.data?.records)
      ? response.data.records
      : [];
  }

  private mapRecordToRow(
    record: SubmissionTableLabRecord,
    index: number,
    overrides?: Partial<Pick<SubmissionTableLabRow, 'actionStatus' | 'isCheckingActionStatus'>>
  ): SubmissionTableLabRow {
    const id = this.readIdentifier(record, ['submission_id', 'submissionid', 'submission_no', 'quote_id'])
      || `SUB-${index + 1}`;
    const quoteId = this.readIdentifier(record, ['quote_id']) || 'N/A';
    const applicationType = this.readIdentifier(record, ['application_type_id']) || 'N/A';
    const createdAtValue = this.readString(record, ['submit_date', 'created_at', 'created_date', 'createdon']) || null;

    return {
      id,
      quoteId,
      applicationType,
      ...this.getDummyStatusStages(applicationType, index),
      policyNumber: this.readString(record, ['policy_number']) || 'N/A',
      insured: this.readString(record, ['insured_name', 'insured', 'name_insured']) || 'N/A',
      underwriter: this.readString(record, ['underwriter', 'underwriter_name', 'assigned_underwriter']) || 'N/A',
      agency: this.readString(record, ['agency', 'agency_name', 'short_name']) || 'N/A',
      line: this.readString(record, ['lob', 'lob_type', 'line_of_business']) || 'N/A',
      submissionType: this.readString(record, ['quote_desc', 'submission_type', 'treaty_description']) || 'N/A',
      effectiveDate: this.readString(record, ['effective_date', 'effectiveDate']) || 'N/A',
      annualRevenue: this.readNumber(record, ['premium', 'annual_revenue']),
      createdAt: this.formatCreatedAt(createdAtValue),
      createdAtValue,
      riskAppetite: this.readString(record, ['risk_appetite', 'riskappetite']) || 'N/A',
      bindPropensity: this.readString(record, ['bind_propensity', 'bindpropensity']) || 'N/A',
      dataQualityScore: this.readString(record, ['data_quality_score', 'dataqualityscore']) || 'N/A',
      evaluationStatus: 'N/A',
      submissionStatus: this.readString(record, ['classified_status', 'submission_status', 'status']) || 'N/A',
      actionStatus: overrides?.actionStatus ?? null,
      isCheckingActionStatus: overrides?.isCheckingActionStatus ?? this.hasCheckableQuoteId(quoteId)
    };
  }

  private mapSubmissionActionStatus(response: SubmissionTableLabStatusResponse): SubmissionTableLabRow['actionStatus'] {
    const rawRiskAppetiteScore = response.risk_appetite_score?.final_risk_appetite_score;
    const evaluationStatus = response.submission_acceptance_check?.evaluation_status;
    const evaluationDecision = evaluationStatus === 'success'
      ? response.submission_acceptance_check?.evaluation_result?.decision
      : null;
    const riskAppetiteScore = typeof rawRiskAppetiteScore === 'number'
      ? String(rawRiskAppetiteScore)
      : typeof rawRiskAppetiteScore === 'string' && rawRiskAppetiteScore.trim().length
        ? rawRiskAppetiteScore.trim()
        : null;

    return {
      isProcessed: response.is_processed === true,
      hasDocument: response.document === true,
      riskAppetiteScore,
      evaluationStatus: (evaluationDecision?.trim().length ? evaluationDecision.trim() : 'N/A')
    };
  }

  private getDummyStatusStages(applicationType: string, index: number): Pick<SubmissionTableLabRow, 'statusCurrentStage' | 'statusStageTotal'> {
    const statusStageTotal = this.getStageCountForApplicationType(applicationType);
    const stagePattern = [1, 2, 2, 3, 3, 4, 5];
    const statusCurrentStage = Math.min(stagePattern[index % stagePattern.length], statusStageTotal);

    return {
      statusCurrentStage,
      statusStageTotal
    };
  }

  private getStageCountForApplicationType(applicationType: string): number {
    switch (applicationType) {
      case '499':
        return 5;
      case '485':
      case '492':
        return 4;
      case '495':
      default:
        return 3;
    }
  }

  private readString(record: SubmissionTableLabRecord, keys: string[]): string {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }

    return '';
  }

  private readIdentifier(record: SubmissionTableLabRecord, keys: string[]): string {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }

    return '';
  }

  private readNumber(record: SubmissionTableLabRecord, keys: string[]): number | null {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string') {
        const normalized = value.replace(/[$,]/g, '').trim();

        if (!normalized) {
          continue;
        }

        const parsed = Number(normalized);

        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }

    return null;
  }

  private formatCreatedAt(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  private hasCheckableQuoteId(quoteId: string): boolean {
    return Boolean(quoteId && quoteId !== 'N/A');
  }

  private getEvaluationStatusLabel(row: SubmissionTableLabRow): string {
    switch ((this.getRowActionStatus(row)?.evaluationStatus ?? row.evaluationStatus ?? '').trim().toUpperCase()) {
      case 'PROCEED':
        return 'Proceed';
      case 'FLAGGED':
        return 'Flagged';
      case 'DECLINE':
        return 'Decline';
      default:
        return '';
    }
  }

  private buildSubmissionDetailPayload(submissionId: string): SubmissionTableLabLoadPayload {
    return {
      ...DEFAULT_SUBMISSIONS_PAYLOAD,
      submission_id: submissionId,
      user_id: this.getUserId()
    };
  }

  private normalizeSubmissionId(submissionId: string | number | null | undefined): string {
    if (typeof submissionId === 'string') {
      return submissionId.trim();
    }

    if (typeof submissionId === 'number' && Number.isFinite(submissionId)) {
      return String(submissionId);
    }

    return '';
  }

  private toTitleCase(value: string): string {
    return value
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private buildPagerItems(currentPage: number, totalPages: number): Array<number | 'ellipsis'> {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const items: Array<number | 'ellipsis'> = [1];
    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);

    if (windowStart > 2) {
      items.push('ellipsis');
    }

    for (let page = windowStart; page <= windowEnd; page += 1) {
      items.push(page);
    }

    if (windowEnd < totalPages - 1) {
      items.push('ellipsis');
    }

    items.push(totalPages);

    return items;
  }

  private buildActiveFilterItem(
    key: SubmissionTableLabColumnKey,
    rawValue: string
  ): SubmissionTableLabActiveFilterItem | null {
    const value = rawValue.trim();

    if (!value) {
      return null;
    }

    const values = this.getFilterValues(key);

    return {
      key,
      label: this.getColumnLabel(key),
      value: values.join(', '),
      values,
      isMultiValue: values.length > 1
    };
  }

  private applyFilters(rows: SubmissionTableLabRow[], filters: SubmissionTableLabFilters): SubmissionTableLabRow[] {
    if (!Object.keys(filters).length) {
      return rows;
    }

    return rows.filter((row) => this.matchesFilters(row, filters));
  }

  private matchesFilters(row: SubmissionTableLabRow, filters: SubmissionTableLabFilters): boolean {
    for (const [key, rawValue] of Object.entries(filters) as Array<[SubmissionTableLabColumnKey, string]>) {
      const value = rawValue.trim();

      if (!value) {
        continue;
      }

      switch (key) {
        case 'submissionStatus': {
          const selected = value.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
          if (selected.length && !selected.includes(row.submissionStatus.trim().toLowerCase())) {
            return false;
          }
          break;
        }
        case 'applicationType': {
          const selected = value.split(',').map((item) => item.trim()).filter(Boolean);
          if (selected.length && !selected.includes(row.applicationType.trim())) {
            return false;
          }
          break;
        }
        case 'submitDate':
          if (!this.matchesDateFilter(row.createdAtValue ?? row.createdAt, value)) {
            return false;
          }
          break;
        case 'effectiveDate':
          if (!this.matchesDateFilter(row.effectiveDate, value)) {
            return false;
          }
          break;
        case 'annualRevenue':
          if (!this.matchesTextValue(row.annualRevenue == null ? 'N/A' : String(row.annualRevenue), value)) {
            return false;
          }
          break;
        default:
          if (!this.matchesTextValue(String((row as Record<string, unknown>)[key] ?? ''), value)) {
            return false;
          }
      }
    }

    return true;
  }

  private matchesTextValue(value: string, filterValue: string): boolean {
    return value.toLowerCase().includes(filterValue.toLowerCase());
  }

  private matchesDateFilter(value: string | null | undefined, filterValue: string): boolean {
    const normalizedFilter = this.normalizeDate(filterValue);
    const normalizedValue = this.normalizeDate(value ?? '');

    if (!normalizedFilter) {
      return true;
    }

    return normalizedValue === normalizedFilter;
  }

  private normalizeDate(value: string): string {
    const trimmed = value.trim();

    if (!trimmed) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);

    if (Number.isNaN(parsed.getTime())) {
      return trimmed.toLowerCase();
    }

    return parsed.toISOString().slice(0, 10);
  }

  private applySort(rows: SubmissionTableLabRow[], sortState: SubmissionTableLabSortState | null): SubmissionTableLabRow[] {
    if (!sortState) {
      return rows;
    }

    return [...rows].sort((left, right) => {
      const leftValue = this.getSortValue(left, sortState.key);
      const rightValue = this.getSortValue(right, sortState.key);
      const comparison = this.compareSortValues(leftValue, rightValue);
      return sortState.direction === 'desc' ? comparison * -1 : comparison;
    });
  }

  private getSortValue(row: SubmissionTableLabRow, key: SubmissionTableLabColumnKey): number | string {
    if (key === 'annualRevenue') {
      return row.annualRevenue ?? Number.NEGATIVE_INFINITY;
    }

    if (key === 'submitDate' || key === 'effectiveDate') {
      const rawValue = key === 'submitDate'
        ? (row.createdAtValue ?? row.createdAt ?? '')
        : (row.effectiveDate ?? '');
      const timestamp = Date.parse(String(rawValue));
      return Number.isNaN(timestamp) ? String(rawValue) : timestamp;
    }

    return String((row as Record<string, unknown>)[key] ?? '');
  }

  private compareSortValues(left: number | string, right: number | string): number {
    if (typeof left === 'number' && typeof right === 'number') {
      return left - right;
    }

    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }
}
